import { useCallback, useMemo } from 'react';
import {
  DynamicFieldProperty_Enum,
  Template,
  TemplateType_Slug,
} from '@weave/schema-gen-ts/dist/schemas/messaging/templator/v2/model.pb';
import { Vertical } from '@weave/schema-gen-ts/dist/shared/vertical/vertical.pb';
import { AppointmentsApiQueries } from '@frontend/api-appointments';
import { PetsV1 } from '@frontend/api-pets';
import { useTranslation } from '@frontend/i18n';
import {
  ManualTemplateTypes,
  TEMPLATE_TYPE_LINK_MAP,
  useGetDefaultTemplate,
  UseTemplateFlowResult,
} from '@frontend/integrated-messaging';
import { useAppScopeStore } from '@frontend/scope';
import { InboxPrefixes } from '@frontend/tracking-prefixes';
import { useTemplateMessagingStore } from '../stores/template-selector-store';
import { UseTemplateFlowArgs } from '../types';

export const useScheduleTemplateFlow = ({
  groupId,
  personId,
  renderTemplate,
  hasAppointments: providedHasAppointments,
  modalControl,
}: UseTemplateFlowArgs & { hasAppointments?: boolean }): UseTemplateFlowResult => {
  const { t } = useTranslation('inbox-templates');
  const { accessibleLocationData } = useAppScopeStore();
  const isVet = accessibleLocationData[groupId]?.vertical === Vertical.VET;
  const getDefaultTemplate = useGetDefaultTemplate({ groupIds: [groupId] });
  const templateTypes = useMemo(() => {
    const result: ManualTemplateTypes[] = [TemplateType_Slug.MANUAL_APPOINTMENT_REMINDER];
    if (isVet) result.push(TemplateType_Slug.MANUAL_APPOINTMENT_REMINDER_PET);
    return result;
  }, [isVet]);
  const { setSelectedMessageTemplate, setSelectedMessageTemplateType } = useTemplateMessagingStore(
    'setSelectedMessageTemplate',
    'setSelectedMessageTemplateType'
  );
  const handleSelectScheduleTemplate = useCallback(
    (template?: Template) => {
      setSelectedMessageTemplateType?.(TemplateType_Slug.MANUAL_APPOINTMENT_REMINDER);
      setSelectedMessageTemplate?.(template);
    },
    [setSelectedMessageTemplateType, setSelectedMessageTemplate]
  );

  const lastUsedScheduleTemplate = getDefaultTemplate({
    type: isVet ? TemplateType_Slug.MANUAL_APPOINTMENT_REMINDER_PET : TemplateType_Slug.MANUAL_APPOINTMENT_REMINDER,
  });
  const showButton = !!personId;
  const { data: appointments, isLoading } = AppointmentsApiQueries.useListPersonAppointments(
    {
      locationId: groupId,
      personId: personId ?? '',
    },
    {
      enabled: !!personId && providedHasAppointments === undefined,
      select: (data) => {
        return data.appointments;
      },
    }
  );
  const hasQueriesAppointments = !!appointments?.length;
  const hasAppointments = !!hasQueriesAppointments || !!providedHasAppointments;

  const petsQuery = PetsV1.Queries.useFetchPetsByPersonIdQuery(
    {
      personId: personId ?? '',
      locationId: groupId,
    },
    {
      enabled: !!personId && isVet && !!groupId,
    }
  );

  return {
    popoverItem: showButton
      ? {
          iconName: 'calendar',
          label: hasAppointments ? t('Schedule Template') : t('No upcoming appointments'),
          trackingId: `${InboxPrefixes.Thread}-schedule-template-button`,
          onClick: hasAppointments
            ? () => {
                handleSelectScheduleTemplate(lastUsedScheduleTemplate);
              }
            : undefined,
          showLabelOnHover: true,
          disabled: !hasAppointments,
        }
      : undefined,
    tabItem: {
      tabLabel: t('Schedule'),
      disabled: !hasAppointments,
      disabledLabel: t('No upcoming appointments'),
      templateSelectorProps: {
        templateTypes,
        onSelectTemplate: (template: Template) => {
          handleSelectScheduleTemplate(template);
        },
        onInsert: ({ appointment, template }) => {
          const resolvedTemplate = template ?? lastUsedScheduleTemplate;
          const relatedType = Object.keys(TEMPLATE_TYPE_LINK_MAP).includes(resolvedTemplate.templateTypeSlug)
            ? TEMPLATE_TYPE_LINK_MAP[resolvedTemplate.templateTypeSlug as keyof typeof TEMPLATE_TYPE_LINK_MAP]
                ?.relatedType
            : undefined;

          if (resolvedTemplate) {
            const petFromAppointment = appointment?.serviceableId
              ? petsQuery.data?.pets?.find((pet) => pet.petServiceableId === appointment.serviceableId)
              : undefined;
            renderTemplate({
              template: resolvedTemplate,
              linkData:
                relatedType && appointment
                  ? [
                      {
                        relatedId: appointment.appointmentId,
                        relatedType,
                        link: '',
                      },
                    ]
                  : [],
              additionalBindings: appointment
                ? {
                    [DynamicFieldProperty_Enum.EVENT_AT]: appointment.appointmentAt,
                    [DynamicFieldProperty_Enum.PRACTITIONER_NAME]: appointment.practitioner,
                    ...(petFromAppointment?.name && {
                      [DynamicFieldProperty_Enum.PET_NAME]: petFromAppointment.name,
                    }),
                  }
                : undefined,
            });
            modalControl.closeModal();
          }
        },
        appointments: appointments ?? [],
        isLoading,
        groupId,
        groupIds: [groupId],
        personId: personId ?? '',
      },
    },
  };
};
